/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * certificateFiller
 * -----------------
 * Fills a certificate template's placeholders in the browser — the same
 * language the backend's document_render.fill_template used, so every
 * template keeps working unchanged:
 *
 *   {{ key }}                      the value, HTML-escaped; unknown → empty
 *   {{#if key}} … {{/if}}          kept only when key has a value
 *   {{#unless key}} … {{/unless}}  kept only when it has none
 *
 * Blocks resolve before placeholders, so a placeholder inside a dropped block
 * is never substituted. Blocks of the same kind do not nest; an outer
 * `unless` around inner `if`s does.
 *
 * Used by the event wizard's previews now and by the participant's download
 * (Phase 5).
 */

import { liveLogo } from './eventCertificates';
import { withCertificateMap } from './certificateMap';

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g;
const BLOCK = /\{\{\s*#(if|unless)\s+([A-Za-z0-9_.]+)\s*\}\}([\s\S]*?)\{\{\s*\/\1\s*\}\}/g;

function lookup(context, path) {
  let value = context;
  for (const part of path.split('.')) {
    if (value === null || typeof value !== 'object') return null;
    value = value[part];
    if (value === undefined || value === null) return null;
  }
  return value;
}

const present = (value) => !(value === null || value === undefined || value === '' || value === false
  || (Array.isArray(value) && value.length === 0)
  || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0));

const escape = (value) => String(value)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#x27;');

export function fillTemplate(html, context = {}) {
  let text = String(html || '');
  // Each pass resolves the outermost pairs; bounded so a malformed template cannot spin.
  for (let pass = 0; pass < 5; pass += 1) {
    let count = 0;
    text = text.replace(BLOCK, (_, kind, key, body) => {
      count += 1;
      const has = present(lookup(context, key));
      return (kind === 'if' ? has : !has) ? body : '';
    });
    if (!count) break;
  }
  return text.replace(PLACEHOLDER, (_, key) => {
    const value = lookup(context, key);
    return value === null ? '' : escape(value);
  });
}

// Name length → size class, so a long name still fits on one line.
const NAME_TIER_LIMITS = [12, 18, 26, 36, 48];
export function nameTier(name) {
  const length = [...String(name || '').trim()].length;
  const tier = NAME_TIER_LIMITS.findIndex((limit) => length <= limit);
  return tier === -1 ? NAME_TIER_LIMITS.length + 1 : tier + 1;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

/** The date parts of a "YYYY-MM-DD…" wall clock, or null. */
function dateParts(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** The event-date placeholders for one "YYYY-MM-DD…" wall clock (the event's start). */
export function dateValues(prefix, value) {
  const parts = dateParts(value);
  if (!parts) return {};
  const { year, month, day } = parts;
  const name = MONTHS[month - 1];
  return {
    event_date: `${day} ${name} ${year}`,
    event_date_short: `${day} ${name.slice(0, 3)} ${year}`,
    event_year: String(year),
  };
}

const todayWallClock = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** A GeoJSON LineString, stored as an object or as a JSON string; null if it is not one. */
function lineOf(value) {
  let line = value;
  if (typeof line === 'string') {
    try { line = JSON.parse(line); } catch { return null; }
  }
  const coords = line && line.type === 'LineString' && Array.isArray(line.coordinates) ? line.coordinates : null;
  return coords && coords.filter((c) => Array.isArray(c) && Number.isFinite(+c[0]) && Number.isFinite(+c[1])).length >= 2
    ? coords.filter((c) => Array.isArray(c) && Number.isFinite(+c[0]) && Number.isFinite(+c[1])) : null;
}

const ROUTE_SIZE = 1000; // the longer side of the drawing, in its own units
const ROUTE_PAD = 0.08; // room around the line for the Start and Finish markers
const ROUTE_MAX_POINTS = 300;

/**
 * The activity's course (event_activity_route.route_polyline) as values a
 * template can draw: `route_path` (an SVG path), `route_viewbox` (sized to the
 * route's own shape, the longer side 1000) and the Start / Finish points. A
 * template places it in its own map area with
 * <svg viewBox="{{ route_viewbox }}" preserveAspectRatio="xMidYMid meet">.
 * Longitude is scaled by cos(latitude) so the shape is not stretched; north is
 * up. Empty when there is no course.
 */
export function routeValues(routeLine) {
  const all = lineOf(routeLine);
  if (!all) return {};
  const step = Math.max(1, Math.ceil(all.length / ROUTE_MAX_POINTS));
  const coords = all.filter((_, i) => i % step === 0);
  if (coords[coords.length - 1] !== all[all.length - 1]) coords.push(all[all.length - 1]);
  const meanLat = coords.reduce((sum, c) => sum + Number(c[1]), 0) / coords.length;
  const k = Math.cos((meanLat * Math.PI) / 180);
  const xs = coords.map((c) => Number(c[0]) * k);
  const ys = coords.map((c) => -Number(c[1]));
  const minX = Math.min(...xs); const minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX || 1e-9;
  const spanY = Math.max(...ys) - minY || 1e-9;
  const inner = ROUTE_SIZE * (1 - 2 * ROUTE_PAD);
  const scale = inner / Math.max(spanX, spanY);
  const pad = ROUTE_SIZE * ROUTE_PAD;
  const width = spanX * scale + 2 * pad;
  const height = spanY * scale + 2 * pad;
  const pts = xs.map((x, i) => [pad + (x - minX) * scale, pad + (ys[i] - minY) * scale]);
  const f = (n) => n.toFixed(1);
  return {
    route_path: `M${pts.map((p) => `${f(p[0])} ${f(p[1])}`).join(' L')}`,
    route_viewbox: `0 0 ${f(Math.max(width, 1))} ${f(Math.max(height, 1))}`,
    route_start_x: f(pts[0][0]),
    route_start_y: f(pts[0][1]),
    route_end_x: f(pts[pts.length - 1][0]),
    route_end_y: f(pts[pts.length - 1][1]),
  };
}

/**
 * The organization name split for a two-part wordmark, the way the app's own
 * BrandMark sets "AthloHive": a one-word CamelCase name splits at its last
 * capital ("AthloHive" → "Athlo" + "Hive"), and AthloHive however it is typed
 * ("Athlohive", "athlohive") gets its own split. Any other name is the lead
 * alone, with an empty tail.
 */
export function wordmarkParts(name) {
  const text = String(name || '').trim();
  if (/^athlohive$/i.test(text)) return { lead: 'Athlo', tail: 'Hive' };
  const match = /^(\S*[a-z])([A-Z][^A-Z\s]*)$/.exec(text);
  return match ? { lead: match[1], tail: match[2] } : { lead: text, tail: '' };
}

const wordmarkValues = (name) => {
  const { lead, tail } = wordmarkParts(name);
  return { organization_name_lead: lead, organization_name_tail: tail };
};

export const SAMPLE_PARTICIPANT = { first_name: 'Aarav', last_name: 'Sharma' };

/**
 * What a preview fills a template with: the organizer's real event, activity,
 * organization name and logo, and a sample participant — so it reads like the
 * certificate a participant will get, with no `{{ … }}` left showing.
 */
export function previewValues({ form = {}, activity = {}, certificate = {}, organizer = '' }) {
  const name = `${SAMPLE_PARTICIPANT.first_name} ${SAMPLE_PARTICIPANT.last_name}`;
  return {
    ...SAMPLE_PARTICIPANT,
    name,
    name_tier: nameTier(name),
    event: form.title || 'Your event',
    activity: activity.name || 'Your activity',
    organizer: organizer || certificate.organizationName || '',
    organization_name: certificate.organizationName || '',
    organization_logo_url: liveLogo(certificate.logoUrl), // a deleted logo prints none
    ...wordmarkValues(certificate.organizationName),
    city: String(form.location || '').split(',')[0].trim(),
    certificate_no: 'AH-PREVIEW',
    ...dateValues('event', form.start_datetime || todayWallClock()),
    ...courseValues(activity.route?.route_polyline, activity.venue?.venue_name),
    finish_time: '',
    position: '',
    result_line: '',
    hide_results: 'yes',
  };
}

/**
 * The course when there is one; otherwise the venue's name, for a venue pin —
 * plus the styled-fallback flags (no map picture yet; withCertificateMap adds
 * one when the real map has been built).
 */
function courseValues(routeLine, venueName) {
  const route = routeValues(routeLine);
  const venue = String(venueName || '').trim();
  const values = route.route_path ? route : (venue ? { venue_name: venue } : {});
  const { map_image: _omit, ...flags } = withCertificateMap(values, '');
  return flags;
}

/**
 * The values of a real certificate: the participant's first and last name, the
 * event name, its start date and city, the activity name, the organization
 * name and logo, the certificate number, the optional finish time and
 * position, and the activity's course (or venue) for templates with a map.
 * There is no distance: when it matters it is part of the activity's name.
 * `eventDate` is a wall clock ("YYYY-MM-DD…") in the event's own zone. Used by
 * the organizer's live preview before Generate, and by the participant's
 * download.
 */
export function certificateValues({
  firstName = '', lastName = '', eventTitle = '', activityName = '', city = '',
  organizationName = '', logoUrl = '', certificateNo = '', finishTime = '', position = '',
  eventDate = '', routeLine = null, venueName = '',
}) {
  const first = String(firstName || '').trim();
  const last = String(lastName || '').trim();
  const name = [first, last].filter(Boolean).join(' ');
  const finish = String(finishTime || '').trim();
  const place = position === null || position === undefined ? '' : String(position).trim();
  const results = [finish && `Finish time ${finish}`, place && `Position ${place}`].filter(Boolean);
  return {
    first_name: first,
    last_name: last,
    name,
    name_tier: nameTier(name),
    event: eventTitle,
    activity: activityName,
    organizer: organizationName,
    organization_name: organizationName,
    organization_logo_url: liveLogo(logoUrl), // a deleted logo prints none
    ...wordmarkValues(organizationName),
    city: String(city || '').split(',')[0].trim(),
    certificate_no: certificateNo,
    ...dateValues('event', eventDate),
    ...courseValues(routeLine, venueName),
    finish_time: finish,
    position: place,
    result_line: results.join(' · '),
    // Layouts with a separate results card hide it when there is nothing to print.
    hide_results: results.length ? '' : 'yes',
  };
}
